import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import React, { JSX } from 'react';
import { useStatsigExperimentContext } from 'statsig/components';

import { getConversationalSearchUnavailableMessage } from './conversationalSearchMessaging';

// Text-only hint shown whenever the toggle is usable and off — centered white copy.
const HintTooltipContent = (): JSX.Element => (
    <div className="flex items-center justify-center p-3 w-64">
        <p className="text-center text-base font-semibold leading-7 text-white">
            Search and refine using natural language
        </p>
    </div>
);

const DisabledForContentTypeTooltipContent = (): JSX.Element => {
    const { showConversationalSearchAudio } = useStatsigExperimentContext();
    return (
        <div className="flex items-center gap-3 p-3 pl-4">
            <div className="flex flex-col">
                <div className="flex items-center gap-2.5 pb-1">
                    <span className="text-base text-white">
                        {getConversationalSearchUnavailableMessage(showConversationalSearchAudio ?? false)}
                    </span>
                </div>
            </div>
        </div>
    );
};

const StandardSearchTooltipContent = ({ onDismiss }: { onDismiss: () => void }): JSX.Element => (
    <div className="flex items-center gap-3 p-3 pl-4 w-48">
        <div className="flex flex-col">
            <div className="flex items-center gap-2.5 pb-1">
                <span className="text-base font-semibold text-white">Standard Search</span>
            </div>
            <p className="text-sm leading-5 text-gray-400">Switch back to standard search anytime.</p>
            <div className="mt-2 flex items-center gap-2.5">
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-sm font-normal text-white hover:underline cursor-pointer"
                >
                    Switch Back
                </button>
            </div>
        </div>
    </div>
);

interface ConversationalSearchTooltipProps {
    disabledForContentType?: boolean;
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
    trigger: JSX.Element;
}

export default function ConversationalSearchTooltip({
    disabledForContentType,
    enabled,
    setEnabled,
    trigger,
}: ConversationalSearchTooltipProps): JSX.Element {
    // Snapshot `enabled` when the tooltip opens so toggling mid-hover doesn't swap content.
    const [enabledAtHover, setEnabledAtHover] = React.useState(enabled);

    // We need to instantiate a new Provider here to get around BuilderBlock's use of a separate React tree.
    // Builder-rendered tooltips can't access the state/context of the main app (Providers live in the root layout)
    return (
        <Provider>
            <Root
                delayDuration={0}
                onOpenChange={(open) => {
                    if (open) setEnabledAtHover(enabled);
                }}
            >
                <Trigger asChild>{trigger}</Trigger>
                <Portal>
                    <Content side="top" sideOffset={8} className="z-50 max-w-md rounded-xl bg-gray-950 shadow-lg">
                        {disabledForContentType ? (
                            <DisabledForContentTypeTooltipContent />
                        ) : enabledAtHover ? (
                            <StandardSearchTooltipContent onDismiss={() => setEnabled(false)} />
                        ) : (
                            <HintTooltipContent />
                        )}
                        <Arrow className="fill-gray-950" style={{ clipPath: 'inset(0 -10px -10px -10px)' }} />
                    </Content>
                </Portal>
            </Root>
        </Provider>
    );
}
